import * as React from 'react'
import { ReactElement } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import Loading from '../../../../components/Loading'
import useAxios from '../../../../hooks/useAxios'
import useEntity from '../../../../hooks/useEntity'
import useUsers from '../../../../hooks/useUsers'
import useAttributes, { AttributeContextType, AttributeViewType, EntityAttributeType } from '../../../../hooks/useAttributes'
import { sortTiers } from '../../../../components/Form/Tier/TierList'
import PartyForm from '../../../../components/Form/Party/PartyForm'
import { applyFolderIntent } from '../../../components/Input/folderPath'
import { getNaturalPersonValidationSchema, NaturalPersonMutationDto } from '../../../config/partyMutationConfig'
import NaturalPersonFormBody from './NaturalPersonFormBody'
import UnsavedChangesGuard from '../../../components/Form/UnsavedChangesGuard'
import { partyGoBack } from './partyGoBack'

/** v9 edit screen for a natural person (HAM-3911). */
export default function EditNaturalPersonPage(): ReactElement {
    const axios = useAxios()
    const history = useHistory()
    const { id } = useParams<{ fileId?: string; id?: string }>()
    const { t: translate } = useTranslation()
    const users = useUsers()

    const attributes = useAttributes(AttributeViewType.Input, [AttributeContextType.NaturalPerson], undefined, EntityAttributeType.Party)

    const [entity] = useEntity<NaturalPersonMutationDto>(
        id => ({ url: `/party/${id}` }),
        id,
        response => ({ ...response.data, tiers: sortTiers(users, response.data.tiers) }),
    )

    if (!entity.loaded) {
        return <Loading />
    }

    const goBack = partyGoBack(history)

    return (
        <PartyForm<NaturalPersonMutationDto>
            formikProps={{
                initialValues: { addressCountry: 'NL', ...entity.data },
                validationSchema: getNaturalPersonValidationSchema(translate, attributes.activeAttributeDefinitions),
            }}
            onSubmit={async (party: NaturalPersonMutationDto) => {
                await applyFolderIntent(axios, party)
                return await axios.put<string>(`/party/natural/${id}`, party)
            }}
            goBack={goBack}
            attributes={attributes}
        >
            <NaturalPersonFormBody goBack={goBack} attributes={attributes} />
            <UnsavedChangesGuard />
        </PartyForm>
    )
}
